/**
 * Sewing Efficiency — the floor spotlight: Building-Floor selector, Top 3 / Last 3, floor table.
 *
 * The rows are not fetched: the summary view is queried once for the whole factory and this picks
 * the spotlighted floor's lines out of that complete result, which is the re-layout §4.3 permits
 * and nothing more. **The completeness gate is not optional** — when the response is truncated the
 * spotlight refuses to render and says why, because a floor's "whole picture" drawn from a capped
 * result would be a picture of part of it with nothing on screen saying so.
 *
 * The Building-Floor buttons filter the factory bar chart; there is no second chart. The Daily
 * trend select is the keyboard route to what a bar click does (§4.9).
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTable, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ANY_VALUE } from '@/lib/filters';

import { TopBottomLines } from './chart';
import { floorMatrix, floorMatrixColumns, type SewingRow } from './columns';
import type { Metric } from './filters';
import { parseBuildingFloor, type BuildingFloorOption } from './spotlight-state';

/**
 * Building-Floor buttons and the floor-scoped actions that sit with them. Rendered into the shell's
 * toolbar slot, so its gaps match that row's (`gap-x-4 gap-y-2`).
 */
export function FloorSpotlightControls({
  floors,
  selected,
  onSelectFloor,
  chartLines,
  trendLine,
  onTrendLineChange,
  showTable,
  onShowTableChange,
}: {
  floors: readonly BuildingFloorOption[];
  selected: readonly string[];
  onSelectFloor: (floor: string | null) => void;
  /**
   * Lines currently on the bar chart — whole factory or the chosen floor. The Daily trend select
   * lists these so a chart bar is not the only way to open a trend (§4.9).
   */
  chartLines: readonly SewingRow[];
  trendLine: string | null;
  onTrendLineChange: (lineNo: string | null) => void;
  showTable: boolean;
  onShowTableChange: (open: boolean) => void;
}) {
  const chosen = parseBuildingFloor(selected[0] ?? null);

  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
      <div className="flex flex-col gap-0.5">
        {/* A span, not a <label>: a <label> with no single form field to point at is not a label. */}
        <span id="floor-spotlight" className="text-xs font-medium">
          Building-Floor
        </span>
        {/* Keyed on the pair, not the floor number: buildings repeat floor numbers, so a floor-only
            selector silently merges two different rooms into one chart. Re-selecting the lit floor
            clears it, which is what `aria-pressed` says. */}
        <div
          role="group"
          aria-labelledby="floor-spotlight"
          // min-h-9 rather than h-9: a wide factory's floors can need a second line.
          className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border bg-muted px-0.5"
        >
          {floors.map((floor) => {
            const active =
              chosen?.building === floor.building && chosen?.floor === floor.floor;
            return (
              <Button
                key={floor.value}
                type="button"
                size="sm"
                variant={active ? 'default' : 'ghost'}
                aria-pressed={active}
                className="h-7 px-2.5 text-xs"
                onClick={() => {
                  onSelectFloor(active ? null : floor.value);
                }}
              >
                {floor.label}
              </Button>
            );
          })}
        </div>
      </div>

      {chartLines.length === 0 ? null : (
        <div className="flex min-w-48 flex-col gap-0.5">
          <Label htmlFor="line-trend" className="text-xs font-medium">
            Daily trend
          </Label>
          <Select
            value={trendLine ?? ANY_VALUE}
            onValueChange={(next) => {
              onTrendLineChange(next === ANY_VALUE ? null : next);
            }}
          >
            <SelectTrigger id="line-trend" className="w-full">
              <SelectValue placeholder="Choose a line" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_VALUE}>No line chosen</SelectItem>
              {/* Server order, not a client re-sort: the rows arrive in grain order (§4.6) and
                  already match the chart and table beside them. */}
              {chartLines.map((row) => {
                const code = String(row['line_no']);
                return (
                  <SelectItem key={code} value={code}>
                    {String(row['show_line_no'] ?? code)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {chosen === null ? null : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 gap-1.5"
          aria-pressed={showTable}
          onClick={() => {
            onShowTableChange(!showTable);
          }}
        >
          <FontAwesomeIcon icon={faTable} className="size-3.5" aria-hidden />
          {showTable ? 'Hide table' : 'Table view'}
        </Button>
      )}
    </div>
  );
}

/**
 * Panels under the chart: Top 3 / Last 3, plus the transposed floor table when a floor is chosen.
 * Both use the same row set as the bars, so they cannot disagree with the chart.
 */
export function FloorSpotlightPanels({
  chartRows,
  isComplete,
  factory,
  selected,
  metric,
  includePpa,
  available,
  showTable,
  /** False when the chart is on Floor grain — Top/Last 3 are line rankings only. */
  showRanking = true,
  onSelectLine,
}: {
  /** Lines currently on the bar chart (chipped; floor-filtered when a floor is chosen). */
  chartRows: readonly SewingRow[];
  isComplete: boolean;
  factory: string;
  selected: readonly string[];
  metric: Metric;
  includePpa: boolean;
  available: ReadonlySet<string>;
  showTable: boolean;
  showRanking?: boolean;
  onSelectLine: (lineNo: string) => void;
}) {
  const chosen = parseBuildingFloor(selected[0] ?? null);

  if (!isComplete) {
    // No selector and no ranking over a capped result: the floor list would be a partial set
    // offered as the whole, and a reader whose floor was cut off would conclude it did not run.
    return (
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="mt-0.5 size-3.5 shrink-0 text-status-warning"
          aria-hidden
        />
        This result was cut short by the row cap, so the floors it contains are not the whole
        factory and a single floor cannot be shown as a whole picture. Narrow the dates or the
        brand, and the floor selector comes back.
      </p>
    );
  }

  if (chosen !== null && chartRows.length === 0) {
    return (
      <p className="py-2 text-sm text-muted-foreground">
        No line on {chosen.building}-{chosen.floor} ran in this period. Widen the dates, or clear
        the building and brand.
      </p>
    );
  }

  if (chartRows.length === 0) {
    return null;
  }

  return (
    <>
      {showRanking ? (
        <TopBottomLines
          rows={chartRows}
          metric={metric}
          includePpa={includePpa}
          available={available}
          onSelectLine={onSelectLine}
        />
      ) : null}

      {chosen !== null && showTable ? (
        <DataTable
          columns={floorMatrixColumns(chartRows)}
          data={floorMatrix(chartRows, metric, includePpa, available)}
          caption={`Output and efficiency by line on ${factory} ${chosen.building}-${chosen.floor}`}
          getRowId={(row) => row.measure}
        />
      ) : null}
    </>
  );
}
